import { ButtonGroup, IPublicRestaurant, Label, RestaurantUtils, TextField } from '@localina/core';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IReservationDateTime } from '../../interfaces';

interface IProps {
    reservation: IReservationDateTime;
    restaurant: IPublicRestaurant;
    setReservation: Dispatch<SetStateAction<IReservationDateTime>>;
}

const maxNumberOfButtonGroupOptions = 12;

// a narrow frame fits about six; longer runs split into two even rows (10 -> 5 + 5, 12 -> 6 + 6)
const gridColumns = (count: number) => (count <= 6 ? count : Math.ceil(count / 2));

const Participants = (props: IProps) => {
    const { t } = useTranslation();

    const [textFieldVisible, setTextFieldVisible] = useState(false);
    const rawShifts = props.reservation.date
        ? RestaurantUtils.getRawShiftsForDate(props.reservation.date, props.restaurant)
        : [];
    const maxPersonsPerReservation = rawShifts.length
        ? Math.max(...rawShifts.map((shift) => shift.maxPersonsPerReservation || 0))
        : maxNumberOfButtonGroupOptions;

    const canDisplayTextField = rawShifts.length && maxPersonsPerReservation > maxNumberOfButtonGroupOptions;

    const participantsOptions: string[] = [];
    const buttonGroupElementsCount = Math.min(maxNumberOfButtonGroupOptions, maxPersonsPerReservation);
    for (let i = 1; i <= buttonGroupElementsCount; i++) {
        participantsOptions.push(String(i));
    }

    const descriptionOfInputField = t('reservation.datetime.participants.callIfNeedSpaceForMorePeople', {
        countOfPeople: maxPersonsPerReservation,
    });
    const descriptionOfButtonGroup =
        maxPersonsPerReservation <= maxNumberOfButtonGroupOptions
            ? t('reservation.datetime.participants.callIfNeedSpaceForMorePeople', {
                  countOfPeople: maxPersonsPerReservation,
              })
            : t('reservation.datetime.participants.showFieldForInputMorePeople', {
                  countOfPeople: buttonGroupElementsCount,
              });

    const handleParticipants = (value: string | number) => {
        const participants = +value;
        props.setReservation((prev) => ({
            ...prev,
            participants,
        }));
    };

    const handleButtonGroupLabelClick = () => {
        if (canDisplayTextField) {
            setTextFieldVisible(true);
        }
    };

    useEffect(() => {
        if (maxPersonsPerReservation) {
            if (maxPersonsPerReservation < props.reservation.participants) {
                handleParticipants('');
            }
        }
    }, [maxPersonsPerReservation]);

    return (
        <div className={'participants-wrapper'}>
            {!textFieldVisible && (
                <React.Fragment>
                    <div
                        className="widget-grid"
                        style={
                            {
                                '--grid-columns': gridColumns(participantsOptions.length),
                                '--grid-columns-wide': participantsOptions.length,
                            } as React.CSSProperties
                        }
                    >
                        <ButtonGroup
                            value={`${props.reservation.participants}`}
                            options={participantsOptions}
                            onChange={handleParticipants}
                        />
                    </div>
                    <div className="button-group-label">
                        <Label
                            onClick={handleButtonGroupLabelClick}
                            type={canDisplayTextField ? 'link' : 'info'}
                            value={descriptionOfButtonGroup}
                        />
                    </div>
                </React.Fragment>
            )}
            {textFieldVisible && (
                <div className={'text-field-wrapper'}>
                    <TextField
                        label={t('reservation.datetime.participants.inputFieldLabel')}
                        name="participants"
                        type="number"
                        value={`${props.reservation.participants === 0 ? '' : props.reservation.participants}`}
                        onChange={handleParticipants}
                        validate={false}
                        hasError={props.reservation.participants > maxPersonsPerReservation}
                        errorMessageKey={t('reservation.datetime.participants.inputFieldError', {
                            countOfPeople: maxPersonsPerReservation,
                        })}
                        autoFocus
                    />
                    <Label type={'info'} value={descriptionOfInputField} />
                </div>
            )}
        </div>
    );
};

export default Participants;
